import { COLORS } from '@/config/colors';
import type { ProceduralDef } from '../types';
import {
  LOOT_FRAMES, REPAIR_GREEN, SPARK, brilliantCut, heaterPlate, hexCut, lootGlow, mediGem, sparkle,
} from './loot-draw';

/*
 * LOOT language (docs/04 "Tehlike ↔ ganimet dili"): faceted gem cuts, clean
 * light facets, a crisp white 4-point sparkle, a soft cool glow — never a
 * dark core, spikes or a red/orange halo (those mean DANGER). Colours: gem
 * gold, CORE emerald, armour gold plate, repair green. Big pieces and ×2
 * supplies get their own cuts in pickups-big.ts.
 */

/** Cut radius as a share of the texture size */
const CUT_R = 0.34;

export const gemYellow: ProceduralDef = {
  key: 'pickup/gem-yellow',
  width: 48,
  height: 48,
  anchorX: 0.5,
  anchorY: 0.5,
  frames: LOOT_FRAMES,
  draw(ctx, w, _h, frame) {
    const c = w / 2;
    const r = w * CUT_R;
    lootGlow(ctx, c, c, w * 0.48);
    brilliantCut(ctx, c, c, r, COLORS.gemYellow);
    const [sx, sy] = SPARK[frame]!;
    sparkle(ctx, c + sx * r, c + sy * r, 3.5);
  },
};

/** CORE currency (key kept from the old red gem — the economy id is 'red'). */
export const gemRed: ProceduralDef = {
  key: 'pickup/gem-red',
  width: 48,
  height: 48,
  anchorX: 0.5,
  anchorY: 0.5,
  frames: LOOT_FRAMES,
  draw(ctx, w, _h, frame) {
    const c = w / 2;
    const r = w * CUT_R;
    lootGlow(ctx, c, c, w * 0.48);
    hexCut(ctx, c, c, r, COLORS.gemRed);
    const [sx, sy] = SPARK[frame]!;
    sparkle(ctx, c + sx * r, c + sy * r, 4);
  },
};

/** Armour pickup — a faceted gold plate (the HUD armour colour) + sparkle. */
export const pickupShield: ProceduralDef = {
  key: 'pickup/shield',
  width: 52,
  height: 52,
  anchorX: 0.5,
  anchorY: 0.5,
  frames: 1,
  draw(ctx, w) {
    const c = w / 2;
    const s = 17;
    lootGlow(ctx, c, c, w * 0.48);
    heaterPlate(ctx, c, c, s, COLORS.hudArmor);
    sparkle(ctx, c - s * 0.45, c - s * 0.35, 4);
  },
};

/** Repair pickup — a faceted green medi-gem with a white cross. */
export const pickupRepair: ProceduralDef = {
  key: 'pickup/repair',
  width: 48,
  height: 48,
  anchorX: 0.5,
  anchorY: 0.5,
  frames: 1,
  draw(ctx, w) {
    const c = w / 2;
    const r = 17;
    lootGlow(ctx, c, c, w * 0.48);
    mediGem(ctx, c, c, r, REPAIR_GREEN);
    sparkle(ctx, c + r * 0.5, c - r * 0.55, 3.5);
  },
};

/** Magnet trail — a soft white streak behind a pulled gem (tinted per kind). */
export const pickupTrail: ProceduralDef = {
  key: 'pickup/gem-trail',
  width: 64,
  height: 16,
  anchorX: 1,
  anchorY: 0.5,
  frames: 1,
  draw(ctx, w, h) {
    const g = ctx.createLinearGradient(0, 0, w, 0);
    g.addColorStop(0, 'rgba(255,255,255,0)');
    g.addColorStop(1, 'rgba(255,255,255,0.9)');
    ctx.beginPath();
    ctx.moveTo(0, h / 2);
    ctx.quadraticCurveTo(w * 0.6, h * 0.1, w, h * 0.25);
    ctx.lineTo(w, h * 0.75);
    ctx.quadraticCurveTo(w * 0.6, h * 0.9, 0, h / 2);
    ctx.closePath();
    ctx.fillStyle = g;
    ctx.fill();
  },
};
